import { z } from "zod";

// TES-1218 (Ö-407, Emelie 2026-10-09) — the patient's primary coach.
//
// "En lagrad primär coachrelation patient ↔ coach. En primär coach åt
// gången." A LOG, like the doctor handover (doctor-handover.ts): the current
// coach is the latest row, and the change history Ö-407 asks for (previous
// coach, new coach, time, reason) is the same rows.

// How an assignment came about. Ö-407 names the first three; the last two are
// the admin's reasons for a change.
export const COACH_ASSIGNMENT_REASONS = [
  // MVP: exactly one active coach in the clinic — she gets every patient.
  "auto_single_coach",
  // A coach pressed "Tilldela mig" on the shared case.
  "claimed",
  // An admin chose the coach ("Tilldela coach").
  "admin_assigned",
  // The previous coach has left.
  "coach_left",
  // Anything else, with mandatory text.
  "other",
] as const;
export const coachAssignmentReasonSchema = z.enum(COACH_ASSIGNMENT_REASONS);
export type CoachAssignmentReason = z.infer<typeof coachAssignmentReasonSchema>;

// The reasons an admin can pick. The other two are written by the system
// (auto_single_coach) and by the coach's own claim.
export const ADMIN_COACH_ASSIGNMENT_REASONS = [
  "admin_assigned",
  "coach_left",
  "other",
] as const satisfies readonly CoachAssignmentReason[];

// Labels for the admin's history and picker. Our wording, pending Emelie.
export const COACH_ASSIGNMENT_REASON_LABELS: Record<
  CoachAssignmentReason,
  string
> = {
  auto_single_coach: "Automatiskt (enda coachen)",
  claimed: "Coachen tog patienten",
  admin_assigned: "Tilldelad av admin",
  coach_left: "Coachen har slutat",
  other: "Annat",
};

// Copy verbatim from Ö-407.
export const COACH_CLAIM_LABEL = "Tilldela mig";
export const COACH_ASSIGN_LABEL = "Tilldela coach";

// "Tilldela coach" — admin only. The reason is internal, like the doctor
// handover's: nothing here reaches a patient DTO.
export const assignCoachSchema = z
  .object({
    // Validated against an active coach in the patient's clinic at the
    // service layer.
    coachId: z.string().min(1),
    reasonCode: z.enum(ADMIN_COACH_ASSIGNMENT_REASONS),
    reasonText: z.string().trim().max(2000).optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.reasonCode === "other" && !v.reasonText) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["reasonText"],
        message: "reason_text_required",
      });
    }
  });
export type AssignCoachRequest = z.infer<typeof assignCoachSchema>;

// "Tilldela mig" — the coach takes the patient herself. No fields: who and
// why are both implied by the route. Strict so a client cannot smuggle a
// coachId into it.
export const claimCoachSchema = z.object({}).strict();
export type ClaimCoachRequest = z.infer<typeof claimCoachSchema>;

const personSchema = z.object({ id: z.string(), displayName: z.string() });

export const coachAssignmentEntrySchema = z.object({
  id: z.string(),
  toCoach: personSchema,
  // Null on the first assignment.
  fromCoach: personSchema.nullable(),
  reasonCode: coachAssignmentReasonSchema,
  reasonText: z.string().nullable(),
  // Null for the automatic assignment.
  assignedBy: personSchema.nullable(),
  assignedAt: z.string(),
});
export type CoachAssignmentEntry = z.infer<typeof coachAssignmentEntrySchema>;

export const coachAssignmentHistorySchema = z.object({
  // The current primary coach, or null when unassigned (none yet, or the
  // latest one has been deactivated).
  current: personSchema.nullable(),
  entries: z.array(coachAssignmentEntrySchema),
  // The active coaches an admin can choose from.
  coaches: z.array(personSchema),
});
export type CoachAssignmentHistory = z.infer<
  typeof coachAssignmentHistorySchema
>;

export const coachAssignmentResultSchema = coachAssignmentHistorySchema.extend({
  moved: z.object({
    // Open follow-ups that moved to the new coach with the patient.
    followUps: z.number().int().nonnegative(),
  }),
});
export type CoachAssignmentResult = z.infer<typeof coachAssignmentResultSchema>;
